'use client';

import { memo, useEffect, useMemo, useState } from 'react';
import { ReactComponent as CheckIcon } from '@assets/icons/check.svg';
import config from 'config';
import { defaultBasketData, ShoppingCartIcon } from 'constant';
import {
  useAddToShoppingCartMutation,
  useGetShoppingCartQuery,
  useUpdateShoppingCartItemMutation
} from 'services/shoppingCartApi';

import { Box, IconButton, Tooltip, useBoolean } from '@chakra-ui/react';

import { useAppSelector } from 'store/hooks';

import { ProductItem, ShopCartType, ShoppingCartName } from 'types';

import { Loader } from 'ui';

const {
  header: { shoppingCartType }
} = config;

const Icon = ShoppingCartIcon[shoppingCartType as ShoppingCartName];

const iconAnimationTime = 1000;

interface AddToCartButtonProps {
  item: ProductItem;
  iconColor: string;
}

const AddToCartButton = ({
  item: { id, item_type, allow_buying },
  iconColor
}: AddToCartButtonProps) => {
  const [count, setCount] = useState<number>(0);
  const [isSuccess, setSuccess] = useBoolean(false);

  const { userInfo } = useAppSelector((state) => state.persistAuth);
  const isLogged = Boolean(userInfo?.clid) && Boolean(userInfo?.token);

  const { data = defaultBasketData } = useGetShoppingCartQuery('', {
    skip: !isLogged
  });

  const [addToShoppingCart, { isLoading: isAdding }] =
    useAddToShoppingCartMutation();
  const [updateShoppingCartItem, { isLoading: isUpdating }] =
    useUpdateShoppingCartItemMutation();

  const isLoading = isAdding || isUpdating;
  const allowBuying = Boolean(allow_buying);

  const existingShoppingCartItem = useMemo(
    () => data.items.find((shopItem) => shopItem.id === id),
    [data.items, id]
  );

  const updateCount = () => {
    setSuccess.on();

    updateShoppingCartItem({
      invoiceId: data.details.invoice_id ?? 0,
      cartItemId: existingShoppingCartItem?.cart_item_id ?? 0,
      itemId: existingShoppingCartItem?.id ?? 0,
      itemType: existingShoppingCartItem?.item_type ?? ShopCartType.Product,
      qty: count + 1
    }).then(() => {
      setCount(count + 1);
      setTimeout(() => {
        setSuccess.off();
      }, iconAnimationTime);
    });
  };

  const handleAddToCart = () => {
    setSuccess.on();

    addToShoppingCart({
      item_id: id,
      item_type,
      store_id: null,
      qty: 1
    })
      .unwrap()
      .then((res) => {
        if (!res?.error) {
          setCount(1);
          setTimeout(() => {
            setSuccess.off();
          }, iconAnimationTime);
        }
      });
  };

  useEffect(() => {
    setCount(existingShoppingCartItem?.qty ?? 0);
  }, [existingShoppingCartItem]);

  return (
    <Box
      transition='transform .2s'
      position='relative'
      width='30px'
      height='30px'
    >
      {isSuccess && !isLoading ? (
        <Box
          position='absolute'
          top='0'
          left='0'
          width='30px'
          height='30px'
          display='flex'
          alignItems='center'
          justifyContent='center'
          animation='fadeOut 1s ease-in-out'
          key='check-icon'
        >
          <CheckIcon fill={iconColor} width={30} height={30} />
        </Box>
      ) : (
        <Box
          position='absolute'
          top='0'
          left='0'
          width='30px'
          height='30px'
          display='flex'
          alignItems='center'
          justifyContent='center'
          animation='fadeIn 0.3s ease-in-in'
          key='tooltip-button'
        >
          <Tooltip
            label={allowBuying ? '' : 'Not Available For Online Purchase'}
            placement='bottom-end'
            py='12px'
            px='12px'
            width='210px'
            bg='black'
            color='white'
            borderRadius='10px'
            top={1}
            hasArrow
            arrowSize={8}
            arrowShadowColor='black'
          >
            <IconButton
              variant='ghost'
              onClick={count === 0 ? handleAddToCart : updateCount}
              bg='transparent'
              aria-label='add to bag'
              minW='auto'
              width={30}
              height={30}
              icon={<Icon fill={iconColor} width={30} height={30} />}
              isDisabled={!allowBuying}
              _hover={{
                opacity: allowBuying ? 0.7 : 0.4
              }}
              cursor='pointer'
            />
          </Tooltip>
        </Box>
      )}
      {isLoading && <Loader fixed bg='blackOpacity.10' />}
    </Box>
  );
};

export default memo(AddToCartButton);
